import { z } from 'zod';

export const accountSchema = z
  .object({
    /**
     * This is returned in the form of a JWT
     */
    access_token: z.string(),
    expires_at: z.number(),
    /**
     * JWT string that contains the same data found in the profile parameter but encoded
     */
    id_token: z.string(),
    /**
     * JWT string that contains the encoded claims exp, iat, jti, iss, aud, sub, typ, azp, session_state, scope, and sid
     */
    refresh_token: z.string(),
    /**
     * AKA nbf, this specifies the earliest time a token should be considered valid
     */
    'not-before-policy': z.number(),
    provider: z.string(),
    providerAccountId: z.string(),
    refresh_expires_in: z.number(),
    scope: z.string(),
    session_state: z.string(),
    token_type: z.string(),
    type: z.string(),
  })
  /**
   * passthrough is used to avoiding removing fields not explicitly mentioned in the schema
   */
  .passthrough();

export const profileSchema = z
  .object({
    /**
     * The following are a group of claims, name/value pairs that assert information about a subject
     *
     * More information may be found here: https://auth0.com/docs/secure/tokens/json-web-tokens/json-web-token-claims
     */

    /**
     * Registered Claims
     *
     * These are claims registered with the Internet Assigned Numbers Authority (IANA)
     * and defined by the JWT specification to ensure interoperability with third-party, or external, applications.
     *
     * OIDC standard claims are reserved claims.
     *
     * A full list of registered claims may be found here https://www.iana.org/assignments/jwt/jwt.xhtml#claims
     */

    /**
     * (subject): Subject of the JWT (the user)
     */
    sub: z.string(),
    /**
     * (issuer): Issuer of the JWT
     */
    iss: z.string(),
    /**
     * (expiration time): Time after which the JWT expires
     */
    exp: z.number(),
    /**
     * (issued at time): Time at which the JWT was issued; can be used to determine age of the JWT
     */
    iat: z.number(),
    /**
     * (JWT ID): Unique identifier; can be used to prevent the JWT from being replayed (allows a token to be used only once)
     */
    jti: z.string(),
    /**
     * Audience
     */
    aud: z.literal('web-client-new'),
    /**
     * Time when the authentication occurred
     */
    auth_time: z.number(),
    /**
     * Authorized party - the party to which the ID Token was issued
     */
    azp: z.literal('web-client-new'),
    /**
     * Given name(s) or first name(s)
     */
    given_name: z.ostring(),
    /**
     * True if the e-mail address has been verified, otherwise false
     */
    email_verified: z.boolean(),
    /**
     * Shorthand name by which the End-User wishes to be referred to (user's email)
     *
     * Note that the returned value is the user's email
     */
    preferred_username: z.string(),
    /**
     * Session ID
     */
    sid: z.string(),
    /**
     * Full name
     */
    name: z.ostring(),
    /**
     * Preferred e-mail address
     */
    email: z.string().email(),
    /**
     * Access Token hash value
     */
    at_hash: z.string(),
    /** Custom Claims
     *
     * These claims consist of non-registered public or private claims.
     *
     * Public claims are collision-resistant while private claims are subject to possible collisions.
     */

    /**
     * The user's DoneDeal ID
     */
    user_id: z.string(),

    /**
     * Login state at the OpenID Provider (OP)
     */
    session_state: z.string(),

    /**
     * Generally used as a header parameter that identifies type of JWT
     */
    typ: z.literal('ID'),
  })
  /**
   * As above, passthrough is used to avoiding removing fields not explicitly mentioned in the schema
   */
  .passthrough();

export const jwtSchema = z
  .object({
    idToken: z.string(),
    accessToken: z.string(),
    refreshToken: z.string(),
    expiresAt: z.number(),
    id: z.ostring(),
    error: z.ostring(),
    name: z.ostring().nullable(),
    email: z.ostring().nullable(),
    picture: z.ostring().nullable(),
  })
  .passthrough();

export const refreshTokenSchema = z
  .object({
    /**
     * This is returned in the form of a JWT
     */
    access_token: z.string(),
    /**
     * JWT string that contains the encoded claims exp, iat, jti, iss, aud, sub, typ, azp, session_state, scope, and sid
     */
    refresh_token: z.ostring(),
    /**
     * Time in seconds to when the access token expires
     */
    expires_in: z.number(),
    /**
     * Time in seconds to when the refresh token expires
     */
    refresh_expires_in: z.number(),
  })
  .passthrough();
